import { useQuery, useQueryClient } from "react-query";
import {
  getBills,
  getBillsDetails,
  getBillsItems,
  getGoodsReceived,
  getGoodsReceivedDetails,
  getGoodsReceivedItems,
  getPOCategories,
  getPurchaseOrderDetails,
  getPurchaseOrderItems,
  getPurchaseOrders,
  getWaybills,
} from "../../../services/procurement";
import {
  capitalizeFirstLetterOfEachWords,
  extractDateOnly,
  formatDateDDMMYYYY,
} from "../../../helpers";
import {
  getDraftRestockDetails,
  getRestock,
} from "../../../services/inventory";

export const useGetWaybills = (queries) => {
  return useQuery(["getWaybills", queries], () =>
    getWaybills(queries).then((res) => res?.data),
  );
};

export const useGetPurchaseOrders = ({ queries = "", queryParams = {} }) => {
  return useQuery(
    ["getPurchaseOrders", queries, queryParams],
    () => getPurchaseOrders(queries, queryParams).then((res) => res.data),
    { staleTime: Infinity },
  );
};

export const useGetPurchaseOrdersForSelectField = ({ queryParams = {} }) => {
  const queryClient = useQueryClient();

  const loadOptions = async (inputValue) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["getPurchaseOrders", { query: inputValue, ...queryParams }],
        queryFn: () =>
          getPurchaseOrders("", {
            query: inputValue ? inputValue : "",
            ...queryParams,
          }).then((res) => res.data),
        staleTime: Infinity,
      });

      return data?.data?.map((item) => ({
        value: item?.id,
        label: `${item?.po_number} - ${item?.po_date ? formatDateDDMMYYYY(item?.po_date, "/") : ""} - ${item?.supplier?.name ? capitalizeFirstLetterOfEachWords(item?.supplier?.name) : "N/A"}`,
      }));
    } catch (error) {
      return [];
    }
  };

  return {
    loadOptions,
  };
};

export const useGetPurchaseOrderDetails = ({ id }) => {
  return useQuery(
    ["getPurchaseOrderDetails", `${id}`],
    () => getPurchaseOrderDetails(id).then((res) => res?.data?.data),
    { staleTime: Infinity, enabled: id ? true : false },
  );
};

export const useGetPurchaseOrderItems = ({ id }) => {
  return useQuery(
    ["getPurchaseOrderItems", `${id}`],
    () =>
      getPurchaseOrderItems(id).then((res) => {
        const data = res?.data?.data;
        const formattedData = data?.map((product) => {
          const data = {
            product_id: product?.product_id,
            variant_id: product?.variant_id,
            store_id: product?.product_property?.store_id || product?.store?.id,
            product_name: product?.item_name,
            available_quantity:
              product?.product_property?.stock_quantity ||
              product?.variant_property?.stock_quantity,
            unit: product?.unit,
            cost_price:
              product?.cost_price ||
              product?.product_property?.cost_price ||
              product?.variant_property?.cost_price,
            requested_quantity: product?.requested_quantity,
            apply_pricing_to_all_stores:
              product?.apply_pricing_to_all_stores ||
              product?.variant_property?.apply_pricing_to_all_stores ||
              false,
            vat_rate: product?.vat_rate,
          };
          const store_property =
            product?.product_property || product?.variant_property;

          store_property?.pricings?.forEach((pricing) => {
            data[`pricing_${pricing?.pricing_type?.id}`] = {
              price: pricing?.price,
              pricing_type_id: pricing?.pricing_type?.id,
            };
          });
          return data;
        });

        return formattedData;
      }),
    { staleTime: Infinity },
  );
};

export const useGetPOCategories = ({ queries = "" }) => {
  return useQuery(
    ["getPOCategories", queries],
    () =>
      getPOCategories(queries).then((res) => {
        return res?.data;
      }),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetGoodsReceived = ({ queries = "" }) => {
  return useQuery(
    ["getGoodsReceived", queries],
    () => getGoodsReceived(queries).then((res) => res.data),
    { staleTime: Infinity },
  );
};

export const useGetGoodsReceivedDetails = ({ id }) => {
  return useQuery(
    ["getGoodsReceivedDetails", `${id}`],
    () => getGoodsReceivedDetails(id).then((res) => res?.data?.data),
    { staleTime: Infinity },
  );
};

export const useGetGoodsReceivedItems = ({
  id,
  isEditMode = false,
  isEnabled = true,
}) => {
  const fetchItems = isEditMode ? getGoodsReceivedItems : getPurchaseOrderItems;
  const itemKey = isEditMode
    ? "getGoodsReceivedItems"
    : "getPOGoodsReceivedItems";

  return useQuery(
    [itemKey, `${id}`],
    () =>
      fetchItems(id).then((res) => {
        const data = res?.data?.data;
        const formattedData = data?.map((item) => {
          const purchase_order_item = isEditMode
            ? item?.purchase_order_item
            : item;

          const data = {
            po_item_id: purchase_order_item?.id,
            product_name: purchase_order_item?.item_name,
            available_quantity:
              purchase_order_item?.product_property?.stock_quantity ||
              purchase_order_item?.variant_property?.stock_quantity,
            unit: purchase_order_item?.unit,
            cost_price:
              purchase_order_item?.cost_price || purchase_order_item?.unit_cost,
            store_id:
              purchase_order_item?.product_property?.store_id ||
              purchase_order_item?.store?.id,
            requested_quantity: purchase_order_item?.requested_quantity,
            quantity_received: isEditMode ? item?.quantity_received : "",
            prev_quantity_received: isEditMode ? item?.quantity_received : 0,
            remaining_receivable:
              purchase_order_item?.remaining_receivable || 0,
            expiry_date:
              isEditMode && item?.expiry_date
                ? extractDateOnly(item?.expiry_date)
                : null,
            total_received: purchase_order_item?.total_received || 0,
            batch_name: item?.batch_name || "",
            barcode: item?.barcode || "",
            items_number: item?.items_number || [],
          };

          return data;
        });

        return formattedData;
      }),
    { staleTime: Infinity, enabled: id && isEnabled ? true : false },
  );
};

export const useGetBills = ({ queries = "" }) => {
  return useQuery(
    ["getBills", queries],
    () => getBills(queries).then((res) => res.data),
    { staleTime: Infinity },
  );
};

export const useGetBillsDetails = ({ id }) => {
  return useQuery(
    ["getBillsDetails", `${id}`],
    () => getBillsDetails(id).then((res) => res?.data?.data),
    { staleTime: Infinity },
  );
};

export const useGetBillsItems = ({
  id,
  isEditMode = false,
  isEnabled = true,
}) => {
  const fetchItems = isEditMode ? getBillsItems : getPurchaseOrderItems;
  const itemKey = isEditMode ? "getBillsItems" : "getPOBillsItems";

  return useQuery(
    [itemKey, `${id}`],
    () =>
      fetchItems(id).then((res) => {
        const data = res?.data?.data;
        const formattedData = data?.map((item) => {
          const purchase_order_item = isEditMode
            ? item?.purchase_order_item
            : item;

          const data = {
            po_item_id:
              purchase_order_item?.purchase_order_item_id ||
              purchase_order_item?.id,
            product_name: purchase_order_item?.item_name,
            store_id:
              purchase_order_item?.product_property?.store_id ||
              purchase_order_item?.store?.id,
            cost_price:
              purchase_order_item?.cost_price || purchase_order_item?.unit_cost,
            requested_quantity: purchase_order_item?.requested_quantity,
            total_received: purchase_order_item?.total_received || 0,
            total_billed: purchase_order_item?.total_billed || 0,
            bill_quantity: isEditMode ? item?.billed_quantity : "",
            prev_bill_quantity: isEditMode ? item?.billed_quantity : 0,
            remaining_billable: purchase_order_item?.remaining_billable || 0,
          };

          return data;
        });

        return formattedData;
      }),
    { staleTime: Infinity, enabled: id && isEnabled ? true : false },
  );
};

export const useGetRestockLogs = ({ queries = "", queryParams = {} }) => {
  return useQuery(
    ["getRestock", queries, queryParams],
    () => getRestock(queries, queryParams).then((res) => res?.data),
    { staleTime: Infinity },
  );
};

export const useGetDraftRestockDetails = ({ id }) => {
  return useQuery(["getDraftRestockDetails", `${id}`], () =>
    getDraftRestockDetails(id).then((res) => res?.data?.data),
  );
};
